import { supabase } from './supabase';

export interface CargaLogistica {
  id: string;
  codigo: string;
  solicitacao_id?: string;
  fornecedor: string;
  peso?: number | null;
  volume?: number | null;
  data_programada?: string | null;
  status: 'Não chegou' | 'Chegou' | 'Pronta' | 'Descarregando' | 'Finalizado' | 'Cancelado';
  filial?: string;
  ordem_chegada?: number | null;
  hora_ordem_chegada?: string | null;
  prioridade?: number | null;
  ocorrencia?: string | null;
  arquivo_ocorrencia_id?: string | null;
  observacoes?: string | null;
  created_at: string;
  updated_at: string;
}

class CargasLogisticaService {
  // Gerar código automático para carga
  private gerarCodigo(): string {
    const data = new Date();
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    const aleatorio = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `CG-${ano}${mes}${dia}-${aleatorio}`;
  }

  // Converter solicitação aprovada em carga
  async converterSolicitacaoParaCarga(solicitacao: any): Promise<{ data?: CargaLogistica; error?: any }> {
    try {
      // Criar carga com base na solicitação
      const carga: Partial<CargaLogistica> = {
        codigo: this.gerarCodigo(),
        solicitacao_id: solicitacao.id,
        fornecedor: solicitacao.fornecedor || 'Não informado',
        peso: solicitacao.peso ? parseFloat(solicitacao.peso) : null,
        volume: solicitacao.volume ? parseFloat(solicitacao.volume) : null,
        data_programada: solicitacao.data_desejada || solicitacao.data_programada || null,
        status: 'Não chegou',
        filial: solicitacao.filial || 'FILIAL_1',
        prioridade: solicitacao.prioridade || 3,
        observacoes: `Carga gerada automaticamente da solicitação ${solicitacao.codigo || solicitacao.id}`
      };

      const { data, error } = await supabase
        .from('cargas_logistica')
        .insert([carga])
        .select();

      if (error) {
        console.error('Erro ao criar carga:', error);
        return { error };
      }

      return { data: data?.[0] };
    } catch (error) {
      console.error('Erro ao converter solicitação para carga:', error);
      return { error };
    }
  }

  // Buscar todas as cargas
  async buscarTodas(filial?: string): Promise<CargaLogistica[]> {
    try {
      let query = supabase
        .from('cargas_logistica')
        .select(`
          *,
          solicitacoes_agendamento (
            tipo_produto,
            tipo_carga,
            documentos
          )
        `)
        .order('created_at', { ascending: false });

      if (filial) {
        query = query.eq('filial', filial);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Mapear dados para incluir tipo_produto, tipo_carga e documentos da solicitação
      return (data || []).map(carga => ({
        ...carga,
        tipo_produto_solicitacao: carga.solicitacoes_agendamento?.tipo_produto || null,
        tipo_carga_solicitacao: carga.solicitacoes_agendamento?.tipo_carga || null,
        documentos_solicitacao: carga.solicitacoes_agendamento?.documentos || []
      })) as any;
    } catch (error) {
      console.error('Erro ao buscar cargas:', error);
      throw error;
    }
  }

  // Buscar carga por ID
  async buscarPorId(id: string): Promise<CargaLogistica | null> {
    try {
      const { data, error } = await supabase
        .from('cargas_logistica')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao buscar carga:', error);
      throw error;
    }
  }

  // Atualizar carga
  async atualizar(id: string, dados: Partial<CargaLogistica>): Promise<{ data?: CargaLogistica; error?: any }> {
    try {
      const { data, error } = await supabase
        .from('cargas_logistica')
        .update(dados)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Erro ao atualizar carga:', error);
      return { error };
    }
  }

  // Atualizar ordem de chegada
  async atualizarOrdemChegada(id: string, ordem: number, hora: string): Promise<{ data?: CargaLogistica; error?: any }> {
    return this.atualizar(id, {
      ordem_chegada: ordem,
      hora_ordem_chegada: hora,
      status: 'Chegou'
    });
  }

  // Registrar ocorrência
  async registrarOcorrencia(id: string, ocorrencia: string, arquivoId?: string): Promise<{ data?: CargaLogistica; error?: any }> {
    return this.atualizar(id, {
      ocorrencia,
      arquivo_ocorrencia_id: arquivoId
    });
  }

  // Deletar carga
  async deletar(id: string): Promise<{ error?: any }> {
    try {
      const { error } = await supabase
        .from('cargas_logistica')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return {};
    } catch (error) {
      console.error('Erro ao deletar carga:', error);
      return { error };
    }
  }
}

export default new CargasLogisticaService();
